import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/FormPage.css';

const TransactionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clients, setClients] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const [formData, setFormData] = useState({
    client_id: '',
    amount: '',
    payment_method: '',
    transaction_date: new Date().toISOString().split('T')[0],
    description: '',
    reference_number: '',
  });

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [clientsRes, methodsRes] = await Promise.all([
        api.getClients(),
        api.getPaymentMethods()
      ]);
      
      setClients(clientsRes.data.clients || clientsRes.data || []);
      setPaymentMethods(methodsRes.data || []);

      if (isEditMode) {
        const response = await api.getTransaction(id);
        const transaction = response.data;
        setFormData({
          client_id: transaction.client_id || '',
          amount: transaction.amount || '',
          payment_method: transaction.payment_method || '',
          transaction_date: transaction.transaction_date?.split('T')[0] || '',
          description: transaction.description || '',
          reference_number: transaction.reference_number || '',
        });
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      if (isEditMode) {
        await api.updateTransaction(id, formData);
        setSuccess('Transaction updated successfully!');
      } else {
        await api.createTransaction(formData);
        setSuccess('Transaction created successfully!');
      }

      setTimeout(() => {
        navigate('/transactions');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to save transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/transactions');
  };

  if (loading && isEditMode) {
    return (
      <div className="form-page-container">
        <div className="loading">Loading transaction...</div>
      </div>
    );
  }

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <div>
          <h1>{isEditMode ? 'Edit Transaction' : 'Add New Transaction'}</h1>
          <p className="form-page-subtitle">
            {isEditMode ? 'Update transaction information' : 'Create a new transaction record'}
          </p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3 className="form-section-title">Transaction Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Client *</label>
                <select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select Client</option>
                  {clients.map(client => (
                    <option key={client.client_id} value={client.client_id}>
                      {client.client_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  step="0.01"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Payment Method *</label>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select Payment Method</option>
                  {paymentMethods.map(method => (
                    <option key={method.method_code || method.id || method} value={method.method_code || method.method_name || method}>
                      {method.method_name || method}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Transaction Date *</label>
                <input
                  type="date"
                  name="transaction_date"
                  value={formData.transaction_date}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Reference Number</label>
                <input
                  type="text"
                  name="reference_number"
                  value={formData.reference_number}
                  onChange={handleChange}
                  placeholder="e.g., TXN123456"
                  disabled={loading}
                />
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Enter transaction notes or description"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEditMode ? 'Update Transaction' : 'Create Transaction')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionForm;

